/**
 * CR500 Phase 3: StarburstBlock
 * CR308: Added component-level assetId support
 * CR560: Savings indicator is now the preferred chrome treatment.
 * 
 * V2 block implementation for the value starburst badge.
 * Matches V1 DealGiftCard + ValueStarburstBadge parity exactly.
 * 
 * Visibility rules (from CR225):
 * - Renders when effectiveVisibility.showStarburst === true
 * - When no value payload, shows fallback label (not hidden)
 * - When visibility is false, never renders
 * 
 * Asset Resolution (CR308):
 * - First checks context.components.starburst.assetId (per deal-type)
 * - Falls back to theme tokens (global default)
 * - Falls back to system default starburst
 */

import type { FC } from "react";
import type { Deal } from "@/lib/types/deals";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import { ValueStarburstBadge } from "@/components/deals/ValueStarburstBadge";
import { SavingsIndicatorBadge } from "@/components/deals/SavingsIndicatorBadge";
import { calculateEffectiveValue, hasValuation, isRangePercentDeal } from "@/lib/types/deals";
import { getDealCategory } from "@/lib/deals/getDealType";
import { getStarburstFallbackLabel, hasStarburstPayload } from "@/lib/deals/cardVisibility";

// ================================================================
// TYPES
// ================================================================

interface ExtendedDeal extends Deal {
  effective_percent_back?: number | null;
  computed_value_cad?: number | null;
}

// CR308: Component config shape for starburst
interface StarburstComponentConfig {
  assetId?: string | null;
  value?: { enabled?: boolean };
  name?: { enabled?: boolean };
  presentation?: "savings_indicator" | "legacy_starburst";
}

// ================================================================
// HELPERS
// ================================================================

/**
 * Determine the effective value for starburst display.
 * Mirrors V1 logic from DealGiftCard.
 */
function getStarburstValue(deal: ExtendedDeal): number | null {
  const effectiveValue = calculateEffectiveValue(deal);
  const dealHasValuation = hasValuation(deal);
  
  // CR225 Addendum: Return null if no value payload
  // The starburst will still render (with fallback label) but without a number
  if (effectiveValue <= 0 || !dealHasValuation) {
    return null;
  }
  
  return effectiveValue;
}

/**
 * Check if this is a percent-type deal (affects display format).
 */
function isPercentDisplay(deal: Deal): boolean {
  const category = getDealCategory(deal);
  // DISCOUNT deals show "X% off" instead of "+X% value"
  return category === "DISCOUNT" || isRangePercentDeal(deal);
}

// ================================================================
// COMPONENT
// ================================================================

export const StarburstBlock: FC<DealCardBlockProps<ExtendedDeal>> = ({ 
  deal,
  block,
  context,
}) => {
  // Check if block is enabled
  if (!block.enabled) {
    return null;
  }

  // Get starburst value (may be null for fallback label mode)
  const valuePercent = getStarburstValue(deal);
  const isPercent = isPercentDisplay(deal);
  const dealType = deal.deal_type?.toUpperCase() ?? "UNCLASSIFIED";
  
  // CR308: Resolve asset ID from component config (per deal-type override)
  const starburstConfig = (context.components?.starburst as StarburstComponentConfig) ?? {};
  const componentAssetId = starburstConfig.assetId ?? null;
  const presentation = starburstConfig.presentation ?? "savings_indicator";
  
  // CR225 Addendum: When visibility is true, ALWAYS render
  // ValueStarburstBadge handles fallback label internally via forceRender
  const hasValue = valuePercent != null && valuePercent > 0;
  const forceRender = !hasValue; // Force render with fallback label when no value

  return (
    <div 
      className="absolute -left-2 -top-2 z-30"
      data-testid="starburst-block"
      data-block-id={block.id}
      data-component-key={presentation === "legacy_starburst" ? "starburst" : "savings_indicator"}
      data-presentation={presentation}
    >
      {presentation === "legacy_starburst" ? (
        <ValueStarburstBadge
          valuePercent={hasValue ? valuePercent : null}
          size="sm"
          isPercent={isPercent}
          dealType={dealType}
          forceRender={forceRender}
          overrideAssetId={componentAssetId}
        />
      ) : (
        <SavingsIndicatorBadge deal={deal} />
      )}
    </div>
  );
};

// ================================================================
// PARITY HELPERS (for compare mode)
// ================================================================

/**
 * Check if starburst should be visible based on V1 rules.
 * Used by parity checklist.
 */
export function shouldStarburstBeVisible(
  deal: ExtendedDeal,
  showStarburst: boolean
): boolean {
  // CR225: Visibility-controlled, NOT data-gated
  // If showStarburst is true, it renders (with value or fallback)
  // If showStarburst is false, it never renders
  return showStarburst;
}

/**
 * Get starburst display info for parity comparison.
 */
export function getStarburstDisplayInfo(deal: ExtendedDeal): {
  hasPayload: boolean;
  valuePercent: number | null;
  fallbackLabel: string;
  isPercent: boolean;
} {
  const valuePercent = getStarburstValue(deal);
  const hasPayload = hasStarburstPayload(deal);
  const dealType = deal.deal_type?.toUpperCase() ?? "UNCLASSIFIED";
  
  return {
    hasPayload,
    valuePercent,
    fallbackLabel: getStarburstFallbackLabel(dealType),
    isPercent: isPercentDisplay(deal),
  };
}

export default StarburstBlock;
